<script lang="ts" context="module">
    export const actions = {
        focusInput: () => {
            getRootDocument().dispatchEvent(
                new CustomEvent("globalSearch:focus"),
            );
        },
        clearInput: () => {
            getRootDocument().dispatchEvent(
                new CustomEvent("globalSearch:clear"),
            );
        },
    };
</script>

<script lang="ts">
    import { tooltip } from "$/utils/tooltip";
    import { portal } from "$/utils/portal";
    import { TenantStore } from "$/stores/TenantStore.js";
    import { GlobalSearchStore } from "$/stores/GlobalSearchStore";
    import {
        ActiveTopic,
        ActiveTopicArchive,
    } from "$/stores/extension/ActiveTopicStore";
    import { ClusterStore } from "$/stores/ClusterStore";
    import { onMount } from "svelte";
    import { url } from "@roxi/routify";
    import { getRootDocument, generateUrlFromSnippet } from "$/utils/Utils";
    import { isMobile } from "$/utils/isMobile";
    import { clickAction } from "$/utils/clickAction";
    import GlobalSearchItem from "./GlobalSearchItem.svelte";
    import GlobalSearchLoading from "./GlobalSearchLoading.svelte";
    import GlobalSearchItemsHeader from "./GlobalSearchItemsHeader.svelte";

    let clusterTopics: Array<any>;

    let combinedResults: Array<any> = [];
    let searchItems: Array<SvelteComponent> = [];

    let inputDom: HTMLInputElement;
    let inputValue = "";

    let shouldSemanticSearch = false;

    $: hasResults =
        $GlobalSearchStore.snippets?.length > 0 ||
        $GlobalSearchStore.topics?.length > 0 ||
        $GlobalSearchStore.suggestions?.length > 0 ||
        $GlobalSearchStore.comments?.length > 0;
    $: combinedResults = $GlobalSearchStore.topics
        .concat($GlobalSearchStore.snippets)
        .concat($GlobalSearchStore.comments)
        .concat($GlobalSearchStore.suggestions);

    function handleOverlayClick(event) {
        if (event.target.closest(".global-search__content")) return;
        GlobalSearchStore.closeSearchModal();
    }

    // check if user is using windows or mac
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;

    // TODO: Extract global event listeners to a separate file
    // for now we will skip it since when I tested it, prevent default did not work
    // and it was opening browser search bar
    const handleKeyPress = (event) => {
        if (event.metaKey && event.key === "k") {
            event.preventDefault();
            openModalAndFocusInput();
        }

        if (event.ctrlKey && event.key === "k") {
            event.preventDefault();
            openModalAndFocusInput();
        }

        if (event.key === "Escape") {
            GlobalSearchStore.closeSearchModal();
        }
    };

    const handleInputFocus = () => {
        requestAnimationFrame(() => {
            inputDom.focus();
        });
    };

    const handleInputClear = () => {
        requestAnimationFrame(() => {
            inputDom.value = "";
        });
    };

    onMount(() => {
        getRootDocument().addEventListener("keydown", handleKeyPress);

        getRootDocument().addEventListener(
            "globalSearch:focus",
            handleInputFocus,
        );
        getRootDocument().addEventListener(
            "globalSearch:clear",
            handleInputClear,
        );

        return () => {
            getRootDocument().removeEventListener("keydown", handleKeyPress);
            getRootDocument().removeEventListener(
                "globalSearch:focus",
                handleInputFocus,
            );
            getRootDocument().removeEventListener(
                "globalSearch:clear",
                handleInputClear,
            );
        };
    });

    const handleInput = async () => {
        if (!$GlobalSearchStore.initialSearch || inputValue.trim().length > 2) {
            if (inputValue.trim().length === 0) {
                GlobalSearchStore.reset();
            } else {
                let activeTopicSnippets = $ActiveTopic
                    ? [...$ActiveTopic.snippets, ...$ActiveTopicArchive]
                    : [];
                GlobalSearchStore.search(
                    inputValue,
                    clusterTopics,
                    $TenantStore?.active?.id,
                    activeTopicSnippets,
                    shouldSemanticSearch,
                    $GlobalSearchStore.webSearch,
                );
            }
        }

        // reset selected index when user types
        // so that when user presses up or down arrow
        // it starts from the top
        selectedIndex = -1;
    };

    const toggleShouldSemanticSearch = () => {
        shouldSemanticSearch = !shouldSemanticSearch;
        GlobalSearchStore.clear();
        handleInput();
        inputDom.focus();
    };

    $: {
        clusterTopics = $ClusterStore ? ClusterStore.getAllClusterTopics() : [];
        GlobalSearchStore.clear();
        handleInput();
    }

    function openModalAndFocusInput() {
        GlobalSearchStore.openSearchModal();

        requestAnimationFrame(() => {
            inputDom?.focus();
        });
    }

    const generateSnippetUrl = (snippet) => {
        if (snippet.topicId) {
            return $url("/:topicId", {
                topicId: snippet.topicId,
                id: snippet.id,
                archived: snippet.archived,
            });
        } else {
            return $url("/inbox", { id: snippet.id });
        }
    };

    const generateTopicUrl = (topic) => {
        return $url("/:topicId", { topicId: topic.id });
    };

    const generateCommentUrl = (comment) => {
        if (comment.topicId) {
            return $url("/:topicId", {
                topicId: comment.topicId,
                id: comment.id,
                type: "COMMENT",
            });
        } else {
            return $url("/inbox", { id: comment.id, type: "COMMENT" });
        }
    };

    /* keyboard functionality */
    let selectedIndex = -1; // To keep track of the selected item index

    function handleKeyDown(event: KeyboardEvent) {
        if (
            !$GlobalSearchStore ||
            !$GlobalSearchStore.snippets ||
            !$GlobalSearchStore.topics ||
            !$GlobalSearchStore.comments
        )
            return;

        switch (event.key) {
            case "ArrowUp":
                event.preventDefault();
                selectedIndex = Math.max(selectedIndex - 1, -1);
                break;
            case "ArrowDown":
                event.preventDefault();
                const totalResults = combinedResults.length;
                selectedIndex = Math.min(selectedIndex + 1, totalResults - 1);
                break;
            case "Enter":
                if (selectedIndex >= 0) {
                    const selectedItem = combinedResults[selectedIndex];

                    // comment
                    if (selectedItem?.comment) {
                        generateCommentUrl(selectedItem);

                        // snippet
                    } else if (selectedItem?.content) {
                        generateSnippetUrl(selectedItem);

                        // topic
                    } else {
                        generateTopicUrl(selectedItem);
                    }
                }
                break;
            case "Escape":
                GlobalSearchStore.closeSearchModal();
                break;
            default:
                return;
        }

        // focus on the selected item
        if (selectedIndex >= 0) searchItems[selectedIndex].focusItem();
        else inputDom.focus();
    }

    // Update search query on tag click or on ?q= query param change
    $: currentQueryValue = $GlobalSearchStore.searchQuery;
    let previousQueryValue = currentQueryValue || "";

    $: {
        if (currentQueryValue !== previousQueryValue) {
            previousQueryValue = currentQueryValue;
            inputValue = currentQueryValue;
        }
    }
</script>

<!-- global search bar -->
<div class="global-search__button" use:clickAction={openModalAndFocusInput}>
    <div class="global-search__icon">
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            class="search-icon__svg"
        >
            <path
                fill-rule="evenodd"
                d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                clip-rule="evenodd"
            />
        </svg>
    </div>
    {#if !$isMobile}
        <span class="global-search__placeholder-text">Search topics</span>
        {#if isMac}
            <span class="global-search__keyboard-shortcut-text"> ⌘ K </span>
        {:else}
            <span class="global-search__keyboard-shortcut-text">
                Ctrl + K
            </span>
        {/if}
    {/if}
</div>

{#if $GlobalSearchStore.showModal}
    <div
        class="global-search__wrapper"
        role="button"
        tabindex="0"
        use:portal
        on:keydown={handleKeyDown}
        on:click={handleOverlayClick}
    >
        <div class="global-search__content">
            <div class="global-search__content-header">
                <div class="global-search__content-header-wrapper">
                    <input
                        placeholder="Let's find what you're looking for!"
                        type="text"
                        bind:this={inputDom}
                        bind:value={inputValue}
                        on:click={() => (selectedIndex = -1)}
                        on:input={handleInput}
                    />

                    {#if !$GlobalSearchStore.webSearch}
                        <!-- Toggle Button -->
                        <button
                            type="button"
                            use:tooltip={"Enhance with AI"}
                            on:click={toggleShouldSemanticSearch}
                            class="global-search__ai-btn"
                        >
                            <div
                                class="global-search__ai-icon"
                                class:active={shouldSemanticSearch}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                    class="size-5"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423z"
                                    />
                                </svg>
                            </div>
                        </button>
                    {:else}
                        <div
                            use:tooltip={"Web Search"}
                            class="global-search__web-icon"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                class="size-6"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
                                />
                            </svg>
                        </div>
                    {/if}
                </div>
                <span class="active hidden"></span>
            </div>
            <div class="global-search__results">
                {#if !$GlobalSearchStore.webSearch}
                    {#if $GlobalSearchStore.snippets.length === 0 && $GlobalSearchStore.topics.length === 0 && $GlobalSearchStore.comments.length === 0 && !$GlobalSearchStore.initialSearch && !$GlobalSearchStore.loading}
                        <GlobalSearchItemsHeader
                            text="Workspace Results"
                            iconPath={`<path stroke-linecap="round" stroke-linejoin="round" d="M15.75 15.75l-2.489-2.489m0 0a3.375 3.375 0 10-4.773-4.773 3.375 3.375 0 004.774 4.774zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />`}
                        />
                        <div class="global-search__no-results">
                            <span>Nothing found in your current workspace</span>
                        </div>
                    {/if}
                    <GlobalSearchLoading
                        loading={$GlobalSearchStore.snippets.length === 0 &&
                            $GlobalSearchStore.topics.length === 0 &&
                            $GlobalSearchStore.comments.length === 0 &&
                            !$GlobalSearchStore.initialSearch &&
                            $GlobalSearchStore.loading}
                    />
                    {#if hasResults}
                        {#if $GlobalSearchStore.topics?.length > 0 && !shouldSemanticSearch}
                            <GlobalSearchItemsHeader
                                text="Topics"
                                iconPath={`<path stroke-linecap="round" stroke-linejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />`}
                            />
                            {#each $GlobalSearchStore.topics as topic}
                                <GlobalSearchItem
                                    bind:this={searchItems[
                                        combinedResults.indexOf(topic)
                                    ]}
                                    item={topic}
                                    itemType="topic"
                                    generateUrl={generateTopicUrl}
                                />
                            {/each}
                        {/if}
                        {#if !$GlobalSearchStore.initialSearch}
                            {#if ($GlobalSearchStore.loading && $GlobalSearchStore.snippets?.length === 0) || $GlobalSearchStore.snippets?.length > 0}
                                <GlobalSearchItemsHeader
                                    text={shouldSemanticSearch
                                        ? `Related Snippets`
                                        : `Snippets`}
                                    iconPath={`<path stroke-linecap="round" stroke-linejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />`}
                                />
                            {/if}

                            <GlobalSearchLoading
                                loading={$GlobalSearchStore.loading &&
                                    $GlobalSearchStore.snippets?.length === 0}
                            />
                            {#each $GlobalSearchStore.snippets as snippet}
                                <GlobalSearchItem
                                    bind:this={searchItems[
                                        combinedResults.indexOf(snippet)
                                    ]}
                                    item={snippet}
                                    itemType="snippet"
                                    generateUrl={generateSnippetUrl}
                                />
                            {/each}

                            {#if ($GlobalSearchStore.loading && $GlobalSearchStore.comments?.length === 0) || $GlobalSearchStore.comments?.length > 0}
                                <GlobalSearchItemsHeader
                                    text={shouldSemanticSearch
                                        ? `Related Comments`
                                        : `Comments`}
                                    iconPath={`<path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />`}
                                />
                            {/if}

                            <GlobalSearchLoading
                                loading={$GlobalSearchStore.loading &&
                                    $GlobalSearchStore.comments?.length === 0}
                            />
                            {#each $GlobalSearchStore.comments as comment}
                                <GlobalSearchItem
                                    bind:this={searchItems[
                                        combinedResults.indexOf(comment)
                                    ]}
                                    item={comment}
                                    itemType="comment"
                                    generateUrl={generateCommentUrl}
                                />
                            {/each}
                        {/if}
                    {/if}
                {:else if ($GlobalSearchStore.loading && $GlobalSearchStore.suggestions?.length === 0) || $GlobalSearchStore.suggestions?.length > 0}
                    <GlobalSearchItemsHeader
                        text={"Web Results"}
                        iconPath={`<path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />`}
                    >
                        {#if !$GlobalSearchStore.webSearch}
                            <a
                                class="global-search__copilot-link"
                                href="https://collabwriting.com/copilot"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke-width="1.5"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                                    />
                                </svg>
                            </a>
                        {/if}
                    </GlobalSearchItemsHeader>
                    <GlobalSearchLoading
                        loading={$GlobalSearchStore.loading &&
                            $GlobalSearchStore.suggestions?.length === 0}
                    />
                    {#each $GlobalSearchStore.suggestions as suggestion}
                        <GlobalSearchItem
                            bind:this={searchItems[
                                combinedResults.indexOf(suggestion)
                            ]}
                            item={suggestion}
                            itemType="link"
                            generateUrl={generateUrlFromSnippet}
                        />
                    {/each}
                {/if}
            </div>

            {#if hasResults && !$GlobalSearchStore.initialSearch}
                <div class="global-search__footer">
                    <div class="global-search__footer-item">
                        ↑ ↓ to navigate | ↵ to select | esc to close
                    </div>
                </div>
            {/if}
        </div>
    </div>
{/if}

<style>
    .global-search__button {
        @apply m-auto mr-1 flex h-6 cursor-pointer select-none items-center gap-2 rounded-2xl bg-gray-600 pl-2 pr-4 text-white;
    }

    @media (max-width: 768px) {
        .global-search__button {
            @apply pl-4;
        }
    }

    .global-search__placeholder-text {
        @apply m-auto text-sm leading-none opacity-75;
    }

    .global-search__keyboard-shortcut-text {
        @apply ml-2 fill-white text-sm;
    }

    .global-search__content-header-wrapper {
        @apply flex w-full items-center rounded-md bg-gray-100;
    }

    .global-search__ai-btn {
        @apply relative mr-1 inline-flex h-8 w-[64px] items-center justify-center rounded bg-gray-200 shadow-inner transition-colors duration-200 focus:outline-none sm:w-[62px];
    }

    .global-search__ai-icon {
        @apply absolute left-1 top-1 h-6 w-6 transform rounded bg-gray-300 p-0.5 text-gray-400 shadow-md transition-all duration-300;
    }

    .global-search__ai-icon.active {
        @apply translate-x-5 bg-gray-800 text-white sm:translate-x-6;
    }

    .global-search__web-icon {
        @apply relative flex h-8 w-10 items-center justify-center rounded-sm p-2 text-gray-400;
    }

    .global-search__icon {
        @apply h-4 w-4 fill-white;
    }

    .global-search__wrapper {
        @apply fixed left-0 top-0 z-50 grid h-full w-full place-items-center bg-gray-700 bg-opacity-25;
    }

    .global-search__content {
        @apply h-fit max-h-[80vh] w-[90vw] max-w-2xl rounded-md bg-white;
    }

    .global-search__content-header {
        @apply relative flex w-full items-center p-2;
    }

    .global-search__content-header input {
        @apply w-full rounded-md bg-gray-100 p-2 outline-none focus-within:outline-none;
    }

    .global-search__footer {
        @apply sticky bottom-0 flex cursor-default flex-row items-center justify-center gap-2 rounded-b-2xl bg-gray-100 px-2 py-2 text-xs text-gray-400 shadow-sm;
    }

    .global-search__results {
        @apply h-full max-h-[72vh] w-full overflow-y-auto;
    }

    .global-search__no-results {
        @apply flex flex-row rounded-sm px-4 py-2;
    }

    .global-search__copilot-link {
        @apply ml-auto h-4 w-4;
    }
</style>
